import { useMemo } from 'react';
import { Box, Typography, Divider, useTheme } from '@mui/material';

// Parse un contenu de CGU en blocs structurés.
// Patterns reconnus :
//   "1. Titre"           -> heading (titre principal)
//   "1.1. Texte"         -> subsection (paragraphe sous-numéroté)
//   "1.1.1. Texte"       -> subsubsection (variante plus profonde)
//   "* Élément"          -> bullet (puce)
//   "---"                -> divider
//   ""                   -> ligne vide (séparateur souple)
//   autre                -> paragraphe simple
const parseCguContent = (raw) => {
  if (!raw || typeof raw !== 'string') return [];

  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let currentList = null;

  const flushList = () => {
    if (currentList && currentList.items.length > 0) {
      blocks.push(currentList);
    }
    currentList = null;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed === '') {
      flushList();
      blocks.push({ type: 'spacer' });
      return;
    }

    if (/^-{3,}$/.test(trimmed)) {
      flushList();
      blocks.push({ type: 'divider' });
      return;
    }

    const headingMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (headingMatch) {
      flushList();
      blocks.push({
        type: 'heading',
        number: headingMatch[1],
        text: headingMatch[2],
      });
      return;
    }

    const subSubMatch = trimmed.match(/^(\d+\.\d+\.\d+\.?)\s+(.+)$/);
    if (subSubMatch) {
      flushList();
      blocks.push({
        type: 'subsubsection',
        number: subSubMatch[1].endsWith('.') ? subSubMatch[1] : `${subSubMatch[1]}.`,
        text: subSubMatch[2],
      });
      return;
    }

    const subMatch = trimmed.match(/^(\d+\.\d+\.?)\s+(.+)$/);
    if (subMatch) {
      flushList();
      blocks.push({
        type: 'subsection',
        number: subMatch[1].endsWith('.') ? subMatch[1] : `${subMatch[1]}.`,
        text: subMatch[2],
      });
      return;
    }

    const bulletMatch = trimmed.match(/^[*•]\s+(.+)$/);
    if (bulletMatch) {
      if (!currentList) {
        currentList = { type: 'list', items: [] };
      }
      currentList.items.push(bulletMatch[1]);
      return;
    }

    flushList();
    blocks.push({ type: 'paragraph', text: trimmed });
  });

  flushList();

  // Compresse les spacers consécutifs.
  const compacted = [];
  blocks.forEach((block) => {
    if (
      block.type === 'spacer' &&
      compacted.length > 0 &&
      compacted[compacted.length - 1].type === 'spacer'
    ) {
      return;
    }
    compacted.push(block);
  });

  return compacted;
};

function CguContent({ content, sx = {} }) {
  const theme = useTheme();
  const blocks = useMemo(() => parseCguContent(content), [content]);

  if (blocks.length === 0) {
    return null;
  }

  return (
    <Box sx={sx}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography
                key={idx}
                component="h3"
                sx={{
                  mt: idx === 0 ? 0 : 3.5,
                  mb: 1.25,
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', sm: '1.15rem' },
                  color: theme.palette.primary.main,
                  letterSpacing: 0.2,
                  lineHeight: 1.35,
                }}
              >
                {block.number}. {block.text}
              </Typography>
            );

          case 'subsection':
            return (
              <Typography
                key={idx}
                component="p"
                variant="body2"
                sx={{
                  mt: 1.25,
                  mb: 0.5,
                  lineHeight: 1.65,
                  color: 'text.primary',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mr: 0.75,
                  }}
                >
                  {block.number}
                </Box>
                {block.text}
              </Typography>
            );

          case 'subsubsection':
            return (
              <Typography
                key={idx}
                component="p"
                variant="body2"
                sx={{
                  mt: 0.75,
                  mb: 0.25,
                  ml: 1.5,
                  lineHeight: 1.6,
                  color: 'text.primary',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    mr: 0.5,
                  }}
                >
                  {block.number}
                </Box>
                {block.text}
              </Typography>
            );

          case 'list':
            return (
              <Box
                key={idx}
                component="ul"
                sx={{
                  mt: 0.5,
                  mb: 1,
                  pl: 3,
                  '& li': {
                    mb: 0.4,
                    lineHeight: 1.6,
                  },
                  '& li::marker': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {block.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'text.primary' }}
                    >
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            );

          case 'paragraph':
            return (
              <Typography
                key={idx}
                component="p"
                variant="body2"
                sx={{
                  mt: 0.75,
                  mb: 0.5,
                  lineHeight: 1.65,
                  color: 'text.primary',
                }}
              >
                {block.text}
              </Typography>
            );

          case 'divider':
            return (
              <Divider
                key={idx}
                sx={{
                  my: 2.5,
                  borderColor: 'divider',
                }}
              />
            );

          case 'spacer':
            return <Box key={idx} sx={{ height: 6 }} />;

          default:
            return null;
        }
      })}
    </Box>
  );
}

export default CguContent;
